import React, { useState, useEffect, useCallback } from "react";
import Layout from "./common/Layout";
import "../css/InComingOrders.scss";
import Cookies from "js-cookie";
import { setAPIAuthorizationToken } from "../apis/API";
import { get_transactions_list } from "../apis/GetTransactionsList";
import Loading from "./common/Loading";
import Pagination from "./common/Pagination";
import { get_transaction_detail } from "../apis/GetTransactionDetails";

const InComingOrders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);

  // 搜索关键词状态
  const [searchKeyword, setSearchKeyword] = useState("");
  // 实际用于查询的关键词
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 处理未授权访问
  const handleShowModal = () => {
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseShowModal = () => {
    setShowModal(false);
  };

  // 获取交易列表数据
  const fetchTransactionsData = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        search: activeSearchKeyword,
        page: currentPage,
        page_size: pageSize,
      };

      const response = await get_transactions_list(params);

      if (response.data && response.data.code === 200) {
        const data = response.data.data;

        // 转换 API 数据格式为组件需要的格式
        const formattedData = (data.list || []).map((item) => ({
          id: item.id, // 保存交易ID，用于获取详情
          user_id: item.user_id, // 保存用户ID，编辑时必需
          transactionNo: item.seller_number || "-",
          collectionAccount: `${item.collection_account || "-"}${
            item.collection_account_name
              ? "/" + item.collection_account_name
              : ""
          }`,
          merchantNo: item.merchant_account_id || "-",
          receivableAmount: `${item.arrival_amount || 0}/${
            item.arrival_currency || ""
          }`,
          depositAmount: `${item.deposit_amount || 0}/${
            item.deposit_currency || ""
          }`,
          fee: `${item.fee || 0}/${item.deposit_currency || ""}`,
          status: item.process_status || "-",
          payerName: item.payer_name || "-",
          transactionDate: item.transaction_time || "-",
          // 详情信息
          platformName: item.platform_name || "-",
          businessType: item.transaction_type_detail || "-",
          storeName: item.shop_name || "-",
          collectionNo: item.collection_account || "-",
          merchantNoDetail: item.seller_number || "-",
          arrivalCurrency: item.arrival_currency || "-",
          arrivalAmount: item.arrival_amount || "0",
          depositCurrency: item.deposit_currency || "-",
          depositAmountDetail: item.deposit_amount || "0",
          feeAmount: item.fee || "0",
          statusDetail: item.process_status || "-",
          remark: item.remark || "-",
          payerNameDetail: item.payer_name || "-",
          transactionTime: item.transaction_time || "-",
          completionTime: item.transaction_complete_time || "-",
          note: item.note || "-",
        }));

        setTransactionsData(formattedData);
        setTotalItems(data.pagination?.total || 0);
      } else {
        console.error("获取交易列表失败:", response.data?.message);
        setTransactionsData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("获取交易列表出错:", error);
      setTransactionsData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeSearchKeyword]);

  // 当参数变化时重新获取数据
  useEffect(() => {
    fetchTransactionsData();
  }, [fetchTransactionsData]);

  const handleViewDetail = async (record) => {
    try {
      setDetailLoading(true);
      setShowDetailModal(true);

      // 调用接口获取交易详情
      const response = await get_transaction_detail(record.id);

      if (response.data && response.data.code === 200) {
        const detail = response.data.data;

        // 格式化详情数据 - 保留所有API返回的字段
        const formattedDetail = {
          // 基础信息
          id: detail.id,
          user_id: detail.user_id,
          merchant_account_id: detail.merchant_account_id,

          // 交易编号和账号信息
          transactionNo: detail.actual_number || "-",
          actualNumber: detail.actual_number || "-",
          collectionNo: detail.receiving_account || "-",
          collectionAccount: `${detail.receiving_account || "-"}`,
          merchantNo: detail.merchant_account_number || "-",
          merchantNoDetail: detail.merchant_account_number || "-",
          sellerNumber: detail.seller_number || "-",

          // 平台和店铺信息
          platformName: detail.platform_name || "-",
          storeName: detail.shop_name || "-",

          // 业务类型
          businessType: detail.transaction_type_detail || "-",
          transactionTypeDetail: detail.transaction_type_detail || "-",

          // 金额信息
          amount: detail.amount || "0",
          arrivalAmount: detail.arrival_amount || "0",
          arrivalCurrency: detail.arrival_currency || "-",
          receivableAmount: `${detail.arrival_amount || 0}/${
            detail.arrival_currency || ""
          }`,

          creditedAmount: detail.credited_amount || "0",
          depositAmountDetail: detail.credited_amount || "0",
          depositCurrency: detail.credited_currency || "-",
          depositAmount: `${detail.credited_amount || 0}/${
            detail.credited_currency || ""
          }`,

          feeAmount: detail.fee || "0",
          feeCurrency: detail.fee_currency || "-",
          fee: `${detail.fee || 0}/${detail.fee_currency || ""}`,

          // 余额信息
          balanceBefore: detail.balance_before || "0",
          balanceAfter: detail.balance_after || "0",

          // 状态信息
          status:
            detail.status === 1
              ? "入账成功"
              : detail.status === 0
              ? "待处理"
              : "-",
          statusDetail: detail.process_status || "-",
          processStatus: detail.process_status || "-",
          statusText: detail.status_text || "-",
          completedAt: detail.completed_at || "-",

          // 通知信息
          notificationStatus: detail.notification_status || "-",
          notificationText: detail.notification_text || "-",
          note: detail.notification_text || "-",

          // 付款人信息
          payerName: detail.payer_name || "-",
          payerNameDetail: detail.payer_name || "-",

          // 描述和备注
          description: detail.description || "-",
          remark: detail.remark || "-",

          // 时间信息
          transactionTime: detail.created_at || "-",
          transactionDate: detail.created_at || "-",
          createdAt: detail.created_at || "-",
          creditedAmount_: detail.credited_amount_ || "-",
          completionTime: detail.updated_at || "-",
          updatedAt: detail.updated_at || "-",
        };

        setSelectedRecord(formattedDetail);
      } else {
        console.error("获取交易详情失败:", response.data?.message);
        // 如果API调用失败，使用列表数据作为后备
        setSelectedRecord(record);
      }
    } catch (error) {
      console.error("获取交易详情失败:", error);
      // 如果出错，使用列表数据作为后备
      setSelectedRecord(record);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  // 查询按钮处理
  const handleSearch = () => {
    setActiveSearchKeyword(searchKeyword); // 更新实际查询关键词
    setCurrentPage(1); // 重置到第一页
  };

  // 重置按钮处理
  const handleReset = () => {
    setSearchKeyword("");
    setActiveSearchKeyword(""); // 清空实际查询关键词
    setCurrentPage(1); // 重置到第一页
  };

  // 计算总页数
  const totalPages = Math.ceil(totalItems / pageSize);

  // 处理页码变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 处理每页显示数量变化
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // 重置到第一页
  };

  return (
    <Layout>
      <div className="incoming-orders-container">
        {/* 顶部标签 */}
        <div className="top-tabs">
          <div className="tab-item active">账单</div>
        </div>

        {/* 搜索表单 */}
        <div className="search-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="请输入关键词搜索（交易编号、收款账号、卖家编号等）"
              className="form-input"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <button className="btn-primary" onClick={handleSearch}>
              查询
            </button>
            <button className="btn-secondary" onClick={handleReset}>
              重置
            </button>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="action-buttons account-buttons-apply1">
          <button
            className="btn-download"
            onClick={() => {
              handleShowModal();
            }}
          >
            <span className="icon">↓</span> 下载
          </button>
          <button
            className="btn-download"
            onClick={() => {
              handleShowModal();
            }}
          >
            <span className="icon">↓</span> 下载入账凭证
          </button>
        </div>

        {/* 数据表格 */}
        <div className="table-container">
          {loading ? (
            <Loading />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>交易编号</th>
                  <th>收款账号</th>
                  <th>卖家编号</th>
                  <th>到账金额/币种</th>
                  <th>入账金额/币种</th>
                  <th>手续费/币种</th>
                  <th>状态</th>
                  <th>付款人名称</th>
                  <th>交易日期</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {transactionsData.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-data">
                      <div className="empty-content">
                        <div className="empty-icon">
                          <img alt="" src="/home/m4.png" />
                        </div>
                        <div className="empty-text">暂无数据</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactionsData.map((record, index) => (
                    <tr key={index}>
                      <td>{record.transactionNo}</td>
                      <td>{record.collectionAccount}</td>
                      <td>{record.merchantNo}</td>
                      <td>{record.receivableAmount}</td>
                      <td>{record.depositAmount}</td>
                      <td>{record.fee}</td>
                      <td>
                        <span
                          className={`status ${
                            record.status === "入账成功"
                              ? "status-success"
                              : "status-refund"
                          }`}
                        >
                          <span className="status-dot" /> {record.status}
                        </span>
                      </td>
                      <td>{record.payerName}</td>
                      <td>{record.transactionDate}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-detail"
                            onClick={() => handleViewDetail(record)}
                          >
                            详情
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 分页 */}
        {!loading && transactionsData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
      <div className="page-footer">
        <p> © 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </div>

      {/* 详情弹窗 */}
      {showDetailModal && (
        <div className="modal-overlay1" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>详情</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {detailLoading ? (
                <Loading />
              ) : selectedRecord ? (
                <div className="detail-grid">
                  {/* 第一行 - 交易基本信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>交易编号：</label>
                      <span>{selectedRecord.transactionNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>实际编号：</label>
                      <span>{selectedRecord.actualNumber}</span>
                    </div>
                    <div className="detail-item">
                      <label>业务类型：</label>
                      <span>{selectedRecord.businessType}</span>
                    </div>
                  </div>

                  {/* 第二行 - 平台和店铺信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>平台名称：</label>
                      <span>{selectedRecord.platformName}</span>
                    </div>
                    <div className="detail-item">
                      <label>店铺名称：</label>
                      <span>{selectedRecord.storeName}</span>
                    </div>
                    <div className="detail-item">
                      <label>付款人名称：</label>
                      <span>{selectedRecord.payerName}</span>
                    </div>
                  </div>

                  {/* 第三行 - 账号信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>收款账号：</label>
                      <span>{selectedRecord.collectionNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>商户账号编号：</label>
                      <span>{selectedRecord.merchantNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>卖家编号：</label>
                      <span>{selectedRecord.merchant_account_id}</span>
                    </div>
                  </div>

                  {/* 第四行 - 金额信息（到账） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>金额：</label>
                      <span>{selectedRecord.amount}</span>
                    </div>
                    <div className="detail-item">
                      <label>到账金额：</label>
                      <span>{selectedRecord.arrivalAmount}</span>
                    </div>
                    <div className="detail-item">
                      <label>到账币种：</label>
                      <span>{selectedRecord.arrivalCurrency}</span>
                    </div>
                  </div>

                  {/* 第五行 - 金额信息（入账） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>入账金额：</label>
                      <span>{selectedRecord.creditedAmount}</span>
                    </div>
                    <div className="detail-item">
                      <label>入账币种：</label>
                      <span>{selectedRecord.depositCurrency}</span>
                    </div>
                    <div className="detail-item">
                      <label>手续费：</label>
                      <span>{selectedRecord.feeAmount}</span>
                    </div>
                  </div>

                  {/* 第六行 - 手续费和余额信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>手续费币种：</label>
                      <span>{selectedRecord.feeCurrency}</span>
                    </div>
                    <div className="detail-item">
                      <label>变更前余额：</label>
                      <span>{selectedRecord.balanceBefore}</span>
                    </div>
                    <div className="detail-item">
                      <label>变更后余额：</label>
                      <span>{selectedRecord.balanceAfter}</span>
                    </div>
                  </div>

                  {/* 第七行 - 状态信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>状态：</label>
                      <span>{selectedRecord.statusText}</span>
                    </div>
                    <div className="detail-item">
                      <label>处理状态：</label>
                      <span>{selectedRecord.processStatus}</span>
                    </div>
                    <div className="detail-item">
                      <label>通知状态：</label>
                      <span>{selectedRecord.notificationStatus}</span>
                    </div>
                  </div>

                  {/* 第八行 - 时间信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>创建时间：</label>
                      <span>{selectedRecord.createdAt}</span>
                    </div>
                    <div className="detail-item">
                      <label>更新时间：</label>
                      <span>{selectedRecord.updatedAt}</span>
                    </div>
                  </div>

                  {/* 第九行 - 描述和备注 */}
                  <div className="detail-row">
                    <div className="detail-item ">
                      <label>描述：</label>
                      <span>{selectedRecord.description}</span>
                    </div>
                    <div className="detail-item  ">
                      <label>备注：</label>
                      <span>{selectedRecord.remark}</span>
                    </div>
                    <div className="detail-item">
                      <label>通知文本：</label>
                      <span>{selectedRecord.notificationText}</span>
                    </div>
                  </div>

                  {/* 系统ID（调试用，可选） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>交易ID：</label>
                      <span>{selectedRecord.id}</span>
                    </div>
                    <div className="detail-item">
                      <label>用户ID：</label>
                      <span>{selectedRecord.user_id}</span>
                    </div>
                    <div className="detail-item">
                      <label>商户账户ID：</label>
                      <span>{selectedRecord.merchant_account_id}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  暂无数据
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 未授权访问模态框 */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseShowModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🔒</div>
            <h2 className="modal-title">未授权访问</h2>
            <p className="modal-message">您当前没有权限访问此功能</p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InComingOrders;
